import { FLOOR_PRICE_NO_RULE_ID } from '@pubstack/common/src/pbs/floor-rule'
import { CreateStack, Stack } from '@pubstack/common/src/stack'
import { FunctionComponent, useCallback, useState } from 'react'
import { useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useFloorRules } from '~/api/app/floor-rules.api'
import { useSite } from '~/api/app/sites.api'
import { StackInventoryAdUnit, useStackInventory } from '~/api/useStackInventory'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Checkbox } from '@pubstack/ui/components/Checkbox'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { label, prefix, TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Spinner } from '~/components/Spinner'
import { Toggle } from '~/components/Toggle'
import Table, { handleTableSearch, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { displayWithCurrency } from '~/utils/string'

type FloorPriceSelectionProps = {
  stack: Stack | undefined
  siteId: string
  isToggleChecked: boolean
  setIsToggleChecked: (checked: boolean) => void
}

const SELECT_FLOOR_RULE_TRACKING_LABEL = (stack: Stack | undefined, isStandard: boolean) => `pbs-${stack ? 'update' : 'create'}-stack-select-${isStandard ? 'standard' : 'specific'}-floor-rule`

export const FloorPriceSelection: FunctionComponent<FloorPriceSelectionProps> = ({ stack, siteId, isToggleChecked, setIsToggleChecked }) => {
  // Hooks
  const { data: floorRules, isLoading: floorRulesLoading } = useFloorRules()
  const user = useAuthenticatedUser()
  const { data: site, isLoading: siteLoading } = useSite(siteId || '', user.currentScopeId)
  // The one inventory this media's stack is built from, which is what its floor rules have to name.
  const { adUnits, isLoading: adUnitsLoading } = useStackInventory(site)
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useFormContext<CreateStack>()
  const floorRuleConfig = useWatch({ control, name: 'configuration.floorRuleConfig' })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'configuration.floorRuleConfig.specificFloorRuleConfigs',
    rules: {
      validate: {
        value: (formValue) => {
          return formValue.some((f) => f.adUnitIds.length === 0) ? 'Every rule must be linked to at least one ad unit.' : true
        },
      },
    },
  })
  const currencySymbol = useScopeCurrency()
  const modal = useGlobalModal()

  // `fields` is a snapshot taken at the last array operation, so a rule picked in a row's select is not in it.
  // The rows read their values from the watched form state; `fields` only carries the stable React key.
  const specificFloorRuleConfigs = floorRuleConfig?.specificFloorRuleConfigs ?? []

  const floorRulesOptions: SelectOptionProp<string>[] = [
    ...floorRules.map((l) => ({
      label: `${l.name} (${displayWithCurrency((l.floor / 100).toFixed(2), currencySymbol)})`,
      value: l.id,
      trackingLabel: SELECT_FLOOR_RULE_TRACKING_LABEL(stack, false) as TrackingLabel,
    })),
    { label: 'No floor price', value: FLOOR_PRICE_NO_RULE_ID, trackingLabel: SELECT_FLOOR_RULE_TRACKING_LABEL(stack, false) as TrackingLabel },
  ]

  const getRemainingFloorRulesOptions = useCallback(
    (selectedFloorRuleId?: string) => {
      const usedFloorRuleIds = specificFloorRuleConfigs.map((config) => config.floorRuleId)
      if (floorRuleConfig?.standardFloorRuleId) {
        usedFloorRuleIds.push(floorRuleConfig.standardFloorRuleId)
      }
      return floorRulesOptions.filter((option) => !usedFloorRuleIds.includes(option.value) || option.value === selectedFloorRuleId)
    },
    [specificFloorRuleConfigs, floorRuleConfig, floorRulesOptions]
  )

  const openAdUnitsModal = (specificFloorIndex: number) => {
    const selectedRule = getValues(`configuration.floorRuleConfig.specificFloorRuleConfigs.${specificFloorIndex}`)
    const adUnitIdsTakenByAnotherRule = specificFloorRuleConfigs.flatMap((config, index) => (index === specificFloorIndex ? [] : config.adUnitIds))
    modal.open(AdUnitSelectionTable, {
      selectableAdUnits: adUnits.filter((a) => !adUnitIdsTakenByAnotherRule.includes(a.id)),
      selectedAdUnits: adUnits.filter((a) => selectedRule?.adUnitIds?.includes(a.id)),
      onSave(adUnitsToSave) {
        // Only the ad units change here. Writing the whole row back would carry the rule the row was appended
        // with, undoing whatever the user has picked in its select since.
        setValue(
          `configuration.floorRuleConfig.specificFloorRuleConfigs.${specificFloorIndex}.adUnitIds`,
          adUnitsToSave.map((a) => a.id),
          { shouldDirty: true }
        )
        modal.close()
        trigger()
      },
      floorRuleName: floorRulesOptions.find((f) => f.value === selectedRule?.floorRuleId)?.label ?? '',
      trackingLabel: `pbs-${stack ? 'update' : 'create'}-floor-rule-specific-adunits`,
    })
  }

  return (
    <>
      {floorRulesLoading || adUnitsLoading || siteLoading ? (
        <Spinner />
      ) : (
        <div className={'flex flex-col gap-4'}>
          <div className={'flex flex-row items-center gap-1'}>
            <Toggle
              value={isToggleChecked}
              onClick={() => {
                if (isToggleChecked) {
                  remove()
                } else {
                  setValue('configuration.floorRuleConfig.standardFloorRuleId', FLOOR_PRICE_NO_RULE_ID)
                }
                setIsToggleChecked(!isToggleChecked)
              }}
              trackingLabel={isToggleChecked ? `pbs-${stack ? 'update' : 'create'}-floor-rule-unset` : `pbs-${stack ? 'update' : 'create'}-floor-rule-set`}
              title={'Floor price'}
            />
            <p>Apply floor price groups on ad units for this stack</p>
          </div>
          {isToggleChecked && (
            <>
              <Select
                options={getRemainingFloorRulesOptions(floorRuleConfig?.standardFloorRuleId)}
                control={control}
                name="configuration.floorRuleConfig.standardFloorRuleId"
                label="Standard rule"
                rules={{
                  required: 'You must select a rule.',
                }}
                error={errors?.configuration?.floorRuleConfig?.standardFloorRuleId?.message}
              />
              {fields.map((field, index) => {
                const adUnitIds = specificFloorRuleConfigs[index]?.adUnitIds ?? []
                return (
                  <div key={field.id} className={'flex items-center gap-2 [&>div]:flex-1'}>
                    <Select
                      options={getRemainingFloorRulesOptions(specificFloorRuleConfigs[index]?.floorRuleId)}
                      control={control}
                      name={`configuration.floorRuleConfig.specificFloorRuleConfigs.${index}.floorRuleId`}
                      label={`Specific rule`}
                    />

                    <Button
                      className={'ml-1.5'}
                      variant={'secondary'}
                      iconName="add_adUnit"
                      onClick={() => openAdUnitsModal(index)}
                      trackingLabel={`pbs-${stack ? 'update' : 'create'}-floor-rule-specific-adunits-edit`}
                    >
                      Select ad units {adUnitIds.length ? `(${adUnitIds.length})` : ''}
                    </Button>
                    <Button
                      className={'ml-1.5'}
                      variant={'tertiary'}
                      iconName={'delete'}
                      onClick={() => remove(index)}
                      trackingLabel={`pbs-${stack ? 'update' : 'create'}-floor-rule-specific-delete`}
                    />
                  </div>
                )
              })}
              <div>
                <Button
                  variant="secondary"
                  trackingLabel={`pbs-${stack ? 'update' : 'create'}-floor-rule-specific-new`}
                  disabled={getRemainingFloorRulesOptions().length === 0}
                  onClick={() => {
                    append({ floorRuleId: getRemainingFloorRulesOptions()?.[0]?.value, adUnitIds: [] })
                  }}
                >
                  Add specific rule
                </Button>
              </div>
              {errors.configuration?.floorRuleConfig?.specificFloorRuleConfigs?.root?.message && (
                <span className={'inline-flex items-center gap-1 font-roboto text-sm/[22px] text-alert'}>
                  <Icon name={'alert'} width={'0.875rem'} /> {errors.configuration.floorRuleConfig.specificFloorRuleConfigs.root.message}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}

const NO_WRAP_CELL = 'flex flex-row items-center gap-1 whitespace-nowrap'

type AdUnitSelectionTableProps = {
  selectableAdUnits: StackInventoryAdUnit[]
  selectedAdUnits: StackInventoryAdUnit[]
  onSave: (adUnits: StackInventoryAdUnit[]) => unknown
  floorRuleName: string
  trackingLabel: `${prefix}-${label}`
}
const AdUnitSelectionTable: FunctionComponent<AdUnitSelectionTableProps> = ({ selectableAdUnits, selectedAdUnits, onSave, floorRuleName, trackingLabel }) => {
  const [selected, setSelected] = useState<StackInventoryAdUnit[]>(selectedAdUnits)
  const {
    resetField,
    control,
    formState: { isDirty },
  } = useForm<{ search: string }>({
    defaultValues: {
      search: '',
    },
  })
  const { close } = useGlobalModal()
  const search = useWatch({ control, name: 'search' })

  const displayedRows = handleTableSearch(selectableAdUnits, search, ['name'])

  const isSelected = (adUnit: StackInventoryAdUnit) => selected.some((a) => a.id === adUnit.id)

  function getUniqueListBy(arr: StackInventoryAdUnit[]) {
    return [...new Map(arr.map((item) => [item['id'], item])).values()]
  }
  const onSelectAll = () => {
    if (displayedRows.every(isSelected)) {
      setSelected(selected.filter((s) => !displayedRows.some((a) => a.id === s.id)))
    } else {
      setSelected(getUniqueListBy([...selected, ...displayedRows]))
    }
  }

  const columns: TableColumns = [
    {
      name: (
        <div className={NO_WRAP_CELL}>
          <Checkbox
            checked={displayedRows.every(isSelected)}
            onClick={onSelectAll}
            trackingLabel={`${trackingLabel}-all-set`}
            indeterminate={displayedRows.some(isSelected) && !displayedRows.every(isSelected)}
            label={'Ad unit'}
          />
        </div>
      ),
      isSortable: false,
    },
  ]

  const onSelection = (row: StackInventoryAdUnit) => {
    if (isSelected(row)) {
      setSelected(selected.filter((s) => s.id !== row.id))
    } else {
      setSelected([...selected, row])
    }
  }

  return (
    <Modal.Content>
      <Modal.Title>Select ad units for {floorRuleName}</Modal.Title>

      <Modal.Body className={'max-h-[400px] min-h-[400px]'}>
        <form className={'mb-3 flex shrink-0 grow items-center'}>
          <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
          {isDirty && <Button className={'mt-1 ml-4'} iconName={'close'} variant={'tertiary'} onClick={() => resetField('search')} trackingLabel={`${trackingLabel}-search-restore`} />}
        </form>
        <Table columns={columns} isLoading={false}>
          {displayedRows.map((row) => (
            <TableRow
              key={row.id}
              clickable={{
                trackingLabel: `${trackingLabel}-set`,
                onClick: (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onSelection(row)
                },
              }}
            >
              <TableCell>
                <div className={NO_WRAP_CELL}>
                  <Checkbox
                    checked={isSelected(row)}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onSelection(row)
                    }}
                    label={row.name}
                    trackingLabel={`${trackingLabel}-set`}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={`${trackingLabel}adunits-cancel`}>
          Cancel
        </Button>
        <Button onClick={() => onSave(selected)} trackingLabel={`${trackingLabel}-confirm`}>
          Confim
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
